import { NavLink, useLocation } from 'react-router-dom';
// chakra imports
import { Box, useColorModeValue } from '@chakra-ui/react';
import { useWhitelabelConfig } from 'contexts/WhitelabelConfigContext';
import useAuth from 'contexts/useAuth';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMyFeatureFlags } from 'services/api.service';
import { IoIosArrowDown, IoIosArrowForward } from 'react-icons/io';
import SidebarItem from 'components/sidebar/components/SidebarItem';

export function SidebarLinks(props: { routes: RoutesType[]; closeSidebar: () => void }) {
  //   Chakra color mode
  let location = useLocation();
  let textColor = useColorModeValue('gray.200', 'white');
  const [openNames, setOpenNames] = useState<string[]>([]);
  const { config, isLoading: isConfigLoading } = useWhitelabelConfig();
  const { userData } = useAuth();
  // flags por empresa (GET /feature-flags/my) para gatear itens como "Em Lote"
  const { data: companyFlags } = useQuery(['myFeatureFlags'], getMyFeatureFlags, {
    staleTime: 5 * 60 * 1000,
  });

  const { routes } = props;

  // verifies if routeName is the one active (in browser input)
  // boundary-safe: '/charges' não pode ficar ativo em '/charges-batch'
  const activeRoute = (routeName: string) => {
    if (!routeName) return false;
    const path = location.pathname;
    return path.endsWith(routeName) || path.includes(`${routeName}/`);
  };
  const childrenActive = (children: RoutesType[]) => {
    return children.some((child) => child.path && activeRoute(child.path.toLowerCase()));
  };

  // this function creates the links from the secondary accordions (for example auth -> sign-in -> default)
  const createLinks = (routes: RoutesType[], subRoute = false) => {
    return routes
      .filter(({ hideOnSideBar }) => !hideOnSideBar)
      .filter(({ featureFlag }) => !featureFlag || (!isConfigLoading && config?.[featureFlag] === true))
      .filter(({ customerFeature }) => {
        const features = userData?.features as Record<string, boolean> | undefined;
        if (!customerFeature) return true;
        if (features?.[customerFeature]) return true;
        // "Depositar" também aparece quando a conta pode recarregar por cartão
        if (customerFeature === 'deposit' && features?.allowCardRecharge === true) return true;
        return false;
      })
      .filter(({ companyFeature }) => {
        // só mostra se a flag da empresa estiver explicitamente ligada
        if (!companyFeature) return true;
        return companyFlags?.[companyFeature] === true;
      })
      .map((route: RoutesType, index: number) => {
        // O recuo do submenu passa por `indented` nos dois ramos. Durante muito tempo só a
        // folha o aplicava: um grupo aninhado (filho que tem filhos, como "Recorrências"
        // dentro de "Cobranças") saía na margem do primeiro nível e se passava por menu de
        // topo que só aparecia quando o pai era aberto.
        if (route.children?.length) {
          const isOpen = openNames.includes(route.name);

          return (
            <Box key={index}>
              <SidebarItem
                icon={route.icon}
                label={route.name}
                indented={subRoute}
                // Fechado com um filho ativo, o grupo assume o destaque; aberto, cede para a
                // folha — senão dois itens apareciam marcados ao mesmo tempo.
                active={!isOpen && childrenActive(route.children)}
                onClick={() => {
                  setOpenNames(isOpen ? openNames.filter((name) => name !== route.name) : [...openNames, route.name]);
                }}
                trailing={
                  <Box color={textColor} display="flex" alignItems="center" flexShrink={0}>
                    {isOpen ? <IoIosArrowDown /> : <IoIosArrowForward />}
                  </Box>
                }
              />
              {isOpen && createLinks(route.children, true)}
            </Box>
          );
        }

        if (!route.path) {
          return '';
        }

        if (route.layout === '/access' || route.layout === '/auth' || route.layout === '/rtl') {
          return (
            <NavLink
              key={index}
              to={route.layout + route.path}
              onClick={() => {
                props.closeSidebar();
              }}
            >
              <SidebarItem
                icon={route.icon}
                label={route.name}
                indented={subRoute}
                active={activeRoute(route.path.toLowerCase())}
              />
            </NavLink>
          );
        }
        return null;
      });
  };
  //  BRAND
  return <>{createLinks(routes)}</>;
}

export default SidebarLinks;
